import { useCallback, useEffect, useRef, useState } from 'react';

const getSpeechRecognitionCtor = () => {
    if (typeof window === 'undefined') return null;
    return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const joinBaseAndSpoken = (base, spoken) => {
    if (!spoken) return base;
    if (!base) return spoken;
    const trimmedBase = base.replace(/\s+$/, '');
    return `${trimmedBase} ${spoken}`;
};

// Browser-native speech recognition.
// Returns { isListening, isSupported, start, stop, toggle, syncBaseText }.
// `onTranscript(text)` is called with the merged base + interim/final transcript while listening.
// Call `syncBaseText(currentInput)` whenever the consumer's input value changes so the hook can
// detect manual edits made by the user mid-session and rebaseline (otherwise the next `onresult`
// emits the cumulative spoken buffer and overwrites the edit).
// Errors that end the session for good (no point auto-restarting).
const FATAL_RECOGNITION_ERRORS = new Set([
    'not-allowed',
    'service-not-allowed',
    'audio-capture',
    'language-not-supported',
    'bad-grammar',
]);

const resolveLang = lang => lang || (typeof navigator !== 'undefined' && navigator.language) || 'en-US';

const useSpeechRecognition = ({ onTranscript, lang } = {}) => {
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef(null);
    const baseTextRef = useRef('');
    const lastEmittedRef = useRef('');
    const restartingRef = useRef(false);
    // Explicit user intent. The Web Speech API auto-ends sessions on silence,
    // browser-internal timeouts, and recoverable errors; without this flag we
    // only restart for the manual-edit rebaseline path and the user gets
    // kicked out mid-sentence on every brief pause.
    const userWantsListeningRef = useRef(false);
    const onTranscriptRef = useRef(onTranscript);
    const langRef = useRef(lang);
    const isSupportedRef = useRef(!!getSpeechRecognitionCtor());

    useEffect(() => {
        onTranscriptRef.current = onTranscript;
    }, [onTranscript]);

    useEffect(() => {
        langRef.current = lang;
        const recognition = recognitionRef.current;
        if (recognition) recognition.lang = resolveLang(lang);
    }, [lang]);

    useEffect(() => {
        const SpeechRecognition = getSpeechRecognitionCtor();
        if (!SpeechRecognition) return undefined;

        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = resolveLang(langRef.current);

        recognition.onresult = event => {
            let finalText = '';
            let interimText = '';
            for (let i = 0; i < event.results.length; i += 1) {
                const result = event.results[i];
                if (result.isFinal) finalText += result[0].transcript;
                else interimText += result[0].transcript;
            }
            const spoken = `${finalText}${interimText}`.trim();
            const next = joinBaseAndSpoken(baseTextRef.current, spoken);
            lastEmittedRef.current = next;
            onTranscriptRef.current?.(next);
        };

        recognition.onerror = event => {
            if (FATAL_RECOGNITION_ERRORS.has(event?.error)) {
                userWantsListeningRef.current = false;
                restartingRef.current = false;
                setIsListening(false);
            }
            // Recoverable errors (no-speech, aborted, network) fall through
            // to onend, which auto-restarts when the user still wants to listen.
        };

        recognition.onend = () => {
            if (restartingRef.current) {
                restartingRef.current = false;
                try {
                    recognition.start();
                    return;
                } catch (e) {
                    // fall through and mark as not listening
                }
            }
            if (userWantsListeningRef.current) {
                try {
                    recognition.start();
                    return;
                } catch (e) {
                    // Already running or transient state; give up cleanly.
                }
            }
            setIsListening(false);
        };

        recognitionRef.current = recognition;

        return () => {
            recognition.onresult = null;
            recognition.onerror = null;
            recognition.onend = null;
            userWantsListeningRef.current = false;
            try { recognition.stop(); } catch (e) { /* noop */ }
            recognitionRef.current = null;
        };
    }, []);

    const start = useCallback((baseText = '') => {
        const recognition = recognitionRef.current;
        if (!recognition) return;
        baseTextRef.current = baseText;
        lastEmittedRef.current = baseText;
        userWantsListeningRef.current = true;
        try {
            recognition.start();
            setIsListening(true);
        } catch (e) {
            // start() throws if already started; treat as no-op.
        }
    }, []);

    const stop = useCallback(() => {
        const recognition = recognitionRef.current;
        if (!recognition) return;
        userWantsListeningRef.current = false;
        restartingRef.current = false;
        try { recognition.stop(); } catch (e) { /* noop */ }
    }, []);

    const toggle = useCallback(
        (baseText = '') => {
            if (isListening) stop();
            else start(baseText);
        },
        [isListening, start, stop]
    );

    // If the consumer mutates the input out-of-band (manual edit/delete) while listening,
    // restart the recognition session so the cumulative result buffer is cleared and future
    // speech is appended to the edited text rather than overwriting it.
    const syncBaseText = useCallback(currentInput => {
        if (!isListening) return;
        const recognition = recognitionRef.current;
        if (!recognition) return;
        if (currentInput === lastEmittedRef.current) return;
        baseTextRef.current = currentInput;
        lastEmittedRef.current = currentInput;
        restartingRef.current = true;
        try { recognition.abort(); } catch (e) { /* noop */ }
    }, [isListening]);

    return {
        isListening,
        isSupported: isSupportedRef.current,
        start,
        stop,
        toggle,
        syncBaseText,
    };
};

export default useSpeechRecognition;
